import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import InputText from 'LuxKit/Typography/InputText'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { formToObject } from 'lib/forms/formToObject'
import { useAppSelector } from 'hooks/reduxHooks'
import useAllUserCartItemsMetadata from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { getCartMachineActor } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'

export const MAX_CART_NAME_LENGTH = 25

type Props = {
  title: string
  description: string
  onBack?: () => void
  onClose: () => void
  onAfterSubmit?: () => void
  // once useAllUserCartItemsMetadata is refactored within machine, move this prop with moveCart?: false
  multiCartItems: Array<App.MultiCartItem>
  skipNotification?: boolean
}

function SharedCartsCreateSharedCartModalContent(props: Props) {
  const intl = useIntl()
  const { title, description, multiCartItems, onBack, onClose, onAfterSubmit, skipNotification = false } = props

  const DEFAULT_CART_NAME_SUGGESTION = intl.formatMessage({ id: 'dqo/lEUryo', defaultMessage: 'Our Dream escape', description: 'Default suggestion for shared cart name' })

  const [cartName, setCartName] = useState('')
  // we can not use MultiCartContext.useActorRef() here because it is not available to this modal
  // since this modal get opened from Snackbar, and Snackbar and AppSnackbar is rendered outside of providers
  const multiCartActorRef = getCartMachineActor()
  if (!multiCartActorRef) {
    throw new Error('Multi cart actor ref not found')
  }

  const isCreating = useSelector(multiCartActorRef, (snapshot) =>
    snapshot.matches('creatingAndMovingToMultiCart'),
  )
  const firstName = useAppSelector(state => state.auth.account.givenName)

  const { fetching, items: cartItemsData } = useAllUserCartItemsMetadata(multiCartItems.map(item => item.item))

  const cartNameSuggestions = useMemo(() => {
    const ACCOUNT_NAME_OPTION = intl.formatMessage({ id: '2DU+G5Q5yR', defaultMessage: 'Escape with {firstName}', description: 'Cart name suggestion using user first name or Friends' }, { firstName: firstName || intl.formatMessage({ id: 'Hb1KlAwTko', defaultMessage: 'Friends', description: 'Fallback name when user first name is not available' }) })
    const firstLocationOption = cartItemsData.find(item => item.locationHeading)

    return [
      ...(firstLocationOption?.startDate ? [`${firstLocationOption.locationHeading} ${moment(firstLocationOption.startDate).year()}`] : []),
      ACCOUNT_NAME_OPTION,
      DEFAULT_CART_NAME_SUGGESTION,
    ]
  }, [cartItemsData, firstName, intl, DEFAULT_CART_NAME_SUGGESTION])

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = formToObject<{ name: string }>(e.currentTarget)
    const cartName = formData.name.trim()

    multiCartActorRef.send({
      type: 'create and move items to target cart',
      cartName,
      multiCartItemIdsToMove: props.multiCartItems.map(item => item.id),
      skipNotification,
    })

    onAfterSubmit?.()
  }

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value.length <= MAX_CART_NAME_LENGTH) {
      setCartName(value)
    }
  }

  const onSuggestionClick = (suggestion: string) => {
    setCartName(suggestion)
  }

  return (
    <>
      <ModalHeader title={title} onBackButtonClick={onBack} onCloseButtonClick={onClose} />
      <ModalBody>
        <ModalContent>
          <form id="create-shared-cart-form" onSubmit={onSubmit}>
            <Group direction="vertical" gap={16}>
              {isCreating && <LoadingIndicator floating opaque />}
              <BodyText variant="large">{description}</BodyText>
              <Group direction="vertical" gap={8}>
                <Group direction="vertical" gap={4}>
                  <TextInput
                    required
                    label={intl.formatMessage({ id: '9Qpfivtfcv', defaultMessage: 'Name shared cart', description: 'Label for shared cart name input' })}
                    name="name"
                    value={cartName}
                    maxLength={MAX_CART_NAME_LENGTH}
                    placeholder={intl.formatMessage({ id: 'uodPhXift1', defaultMessage: 'e.g. Dream Escape', description: 'Placeholder for shared cart name input' })}
                    onChange={onNameChange}
                    invalidErrorMessage={intl.formatMessage({ id: 'u4XA1JWY1p', defaultMessage: 'Please give shared cart a name', description: 'Validation error when shared cart name is empty' })}
                    noValidationSpacing
                    disabled={isCreating}
                  />
                  <InputText variant="caption" colour="neutral-three" align="end">{`${cartName.length}/${MAX_CART_NAME_LENGTH}`}</InputText>
                </Group>
                <Group direction="horizontal" gap={4}>
                  {fetching && <LoadingIndicator inline opaque />}
                  {!fetching && cartNameSuggestions.map((suggestion) => (
                    <AssistChip
                      key={suggestion}
                      size="small"
                      variant="filled"
                      onClick={() => onSuggestionClick(suggestion)}
                      disabled={isCreating}
                    >
                      {suggestion}
                    </AssistChip>
                  ))}
                </Group>
              </Group>
            </Group>
          </form>
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: intl.formatMessage({ id: 'vtd8eV8gT9', defaultMessage: 'Create shared cart', description: 'Primary action button to create a new shared cart' }),
          type: 'submit',
          form: 'create-shared-cart-form',
          disabled: isCreating,
        }}
        secondaryActionProps={{
          children: intl.formatMessage({ id: 'Ejq8PEH8zO', defaultMessage: 'Cancel', description: 'Secondary action button to cancel creating a shared cart' }),
          onClick: onClose,
          disabled: isCreating,
        }}
      />
    </>
  )
}

export default SharedCartsCreateSharedCartModalContent
